import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Finding, MapRoad } from "../api/types";
import { CONDITION_COLOR, ConditionPill, PriorityChip, SeverityChip, StatusChip } from "../components/Badges";
import { CountBars } from "../components/Charts";
import { DataTable } from "../components/DataTable";
import { LinkTabs, PageHeader, Panel } from "../components/Layout";
import { ConditionLegend, MapPanel } from "../components/MapPanel";
import { EmptyState, QueryState, SkeletonBlock } from "../components/States";
import { chainage, km } from "../lib/format";
import { COLORS } from "../styles/themeColors";

type Lens = "condition" | "safety" | "assets";

interface RoadRow { survey_uuid: string; road_code: string; road_name: string; agency: string; county: string; length_km: number; condition: string; verified: number; unreviewed: number; high: number; per_km: number | null }
interface Priority { survey_uuid: string; road_code: string; road_name: string; start_m: number; end_m: number; condition_band: string; priority: number | null; priority_band: string | null; treatment: string | null; indicative_cost_kes: number | null }
interface NetworkResp { lens: Lens; roads: RoadRow[]; by_type: { type_name: string; verified: number; unreviewed: number }[]; findings: Finding[]; priorities: Priority[]; map: MapRoad[] }

const COPY: Record<Lens, { title: string; sub: string; types: string; roads: string }> = {
  condition: { title: "Road condition", sub: "Pavement defects and condition sections across your roads.", types: "Defects by type", roads: "Roads by verified defects" },
  safety: { title: "Road safety", sub: "Hazards and safety features found along your roads.", types: "Safety findings by type", roads: "Roads by safety findings" },
  assets: { title: "Assets & inventory", sub: "Signs, barriers, culverts and other road furniture.", types: "Assets by type", roads: "Roads by assets recorded" },
};

export function NetworkPage() {
  const { lens = "condition" } = useParams();
  const l = (["condition", "safety", "assets"].includes(lens) ? lens : "condition") as Lens;
  const nav = useNavigate();
  const q = useQuery({ queryKey: ["network", l], queryFn: () => api<NetworkResp>(`/api/network/${l}`) });

  return (
    <>
      <PageHeader title="Network" subtitle={COPY[l].sub} />
      <LinkTabs label="Assessment" tabs={[
        { to: "/network/condition", label: "Road condition" },
        { to: "/network/safety", label: "Road safety" },
        { to: "/network/assets", label: "Assets & inventory" },
      ]} />
      <QueryState query={q} skeleton={<SkeletonBlock rows={10} height={24} />}>
        {(d) => (
          <>
            <div className="split">
              <Panel title="Map" id="nw-map">
                <MapPanel
                  label={`${COPY[l].title} map`}
                  height={400}
                  lines={d.map.map((r) => ({ id: r.survey_uuid, points: r.points, color: CONDITION_COLOR[r.condition], label: r.road_code }))}
                  pins={d.findings.filter((f) => f.lat && f.lon).map((f) => ({ id: f.id, lat: f.lat!, lon: f.lon!, color: COLORS.navy, label: `${f.road_code} · ${f.type_name} · ${chainage(f.chainage_m)}`, dashed: f.status === "unreviewed" }))}
                  onSelectLine={(id) => nav(`/roads/${id}`)}
                  overlay={<ConditionLegend />}
                  onSelectPin={(id) => { const f = d.findings.find((x) => x.id === id); if (f) nav(`/roads/${f.survey_uuid}/findings?f=${encodeURIComponent(id)}`); }}
                />
              </Panel>
              <Panel title={COPY[l].types} id="nw-types"><CountBars rows={d.by_type} empty="Nothing recorded yet" /></Panel>
            </div>

            {l === "condition" ? (
              <Panel title="Maintenance priorities" id="nw-prio" flush>
                <DataTable<Priority>
                  rows={d.priorities}
                  rowKey={(r) => `${r.survey_uuid}-${r.start_m}`}
                  onRowClick={(r) => nav(`/roads/${r.survey_uuid}`)}
                  caption="Maintenance priorities"
                  empty={<EmptyState title="No priorities yet">Priorities and treatments appear once the decision engine has assessed the surveys.</EmptyState>}
                  columns={[
                    { key: "road", label: "Road", render: (r) => <>{r.road_code} — {r.road_name}</> },
                    { key: "sec", label: "Section", render: (r) => <span className="mono">{chainage(r.start_m)} – {chainage(r.end_m)}</span> },
                    { key: "c", label: "Condition", render: (r) => <ConditionPill value={r.condition_band} /> },
                    { key: "p", label: "Priority", render: (r) => <><PriorityChip value={r.priority_band} /> <span className="muted num">{r.priority?.toFixed(0)}</span></> },
                    { key: "t", label: "Treatment", render: (r) => r.treatment || "—" },
                    { key: "k", label: "Cost (KES)", align: "right", render: (r) => (r.indicative_cost_kes ? Math.round(r.indicative_cost_kes).toLocaleString("en-KE") : "—") },
                  ]}
                />
              </Panel>
            ) : null}

            <Panel title={COPY[l].roads} id="nw-roads" flush>
              <DataTable<RoadRow>
                rows={d.roads}
                rowKey={(r) => r.survey_uuid}
                onRowClick={(r) => nav(`/roads/${r.survey_uuid}/findings?domain=${l}`)}
                initialSort={{ key: "v", dir: "desc" }}
                caption={COPY[l].roads}
                empty={<EmptyState title="Nothing recorded on any road yet" />}
                columns={[
                  { key: "road", label: "Road", sort: (r) => r.road_code, render: (r) => <Link className="row-link" to={`/roads/${r.survey_uuid}`} onClick={(e) => e.stopPropagation()}>{r.road_code}</Link> },
                  { key: "name", label: "Name", sort: (r) => r.road_name, render: (r) => r.road_name },
                  { key: "ag", label: "Agency", sort: (r) => r.agency, render: (r) => r.agency || "—" },
                  { key: "len", label: "Length", align: "right", sort: (r) => r.length_km, render: (r) => km(r.length_km) },
                  { key: "v", label: "Verified", align: "right", sort: (r) => r.verified, render: (r) => r.verified },
                  { key: "u", label: "Unreviewed", align: "right", sort: (r) => r.unreviewed, render: (r) => <span className="muted">{r.unreviewed}</span> },
                  { key: "pk", label: "Per km", align: "right", sort: (r) => r.per_km, render: (r) => r.per_km?.toFixed(2) ?? "—" },
                  ...(l !== "assets" ? [{ key: "h", label: "High severity", align: "right" as const, sort: (r: RoadRow) => r.high, render: (r: RoadRow) => r.high }] : []),
                ]}
              />
            </Panel>

            <Panel title="All findings" id="nw-all" flush>
              <DataTable<Finding>
                rows={d.findings}
                rowKey={(r) => r.id}
                onRowClick={(r) => nav(`/roads/${r.survey_uuid}/findings?f=${encodeURIComponent(r.id)}`)}
                rowClass={(r) => (r.status === "unreviewed" ? "unreviewed-row" : "")}
                maxHeight={480}
                caption="All findings"
                empty={<EmptyState title="No findings" />}
                columns={[
                  { key: "road", label: "Road", sort: (r) => r.road_code, render: (r) => r.road_code },
                  { key: "ch", label: "Chainage", sort: (r) => r.chainage_m, render: (r) => <span className="mono">{chainage(r.chainage_m)}</span> },
                  { key: "type", label: "Finding", sort: (r) => r.type_name, render: (r) => r.type_name },
                  { key: "sev", label: "Severity", render: (r) => <SeverityChip value={r.severity} /> },
                  { key: "st", label: "Status", sort: (r) => r.status, render: (r) => <StatusChip status={r.status} /> },
                ]}
              />
            </Panel>
          </>
        )}
      </QueryState>
    </>
  );
}
